<template>
  <div class="d-flex flex-column gap-3">
    <div class="mb-2">
      <h3 class="mb-1">
        <v-icon size="small" class="mr-2">{{ truckIcon }}</v-icon>
        {{ getTranslatedCompanyName(assetVisit.company?.name || assetVisit.companyHint) }}
      </h3>
      <v-chip size="x-small" variant="tonal">
        <strong>{{ getAssetVisitTypeDisplayName(assetVisit.visitType) }}</strong>
      </v-chip>
    </div>

    <div class="d-flex flex-column font-size-x-small mb-4" v-if="showAssignSpotSelect">
      <strong class="mb-2">Assign To</strong>
      <yard-view-spot-select @input="$emit('assign', $event)" />
    </div>

    <div v-if="assetVisit.assetContainers?.length > 0">
      <p class="mb-2">
        <v-icon size="x-small">mdi-truck-trailer</v-icon>
        <strong class="font-size-x-small ml-1">{{ t('yard.messages.hookedAssets') }}</strong>
      </p>
      <div
        v-for="assetContainer of assetVisit.assetContainers?.sort((a, b) =>
          a.code.localeCompare(b.code)
        )"
        :key="assetContainer.id"
        class="mb-4">
        <strong>{{ assetContainer.code }}</strong>
        <v-chip size="x-small" variant="tonal" class="text-capitalize ml-2">
          <strong>{{ getTranslatedAssetContainerType(assetContainer.type) }}</strong>
        </v-chip>

        <yard-view-asset-container-status-select
          class="ma-0"
          v-if="allowContainerStatusUpdate"
          :container-status="getAssetContainerStatus(assetContainer.assetContainerEvents)"
          :asset-container="assetContainer"
          @input="updateAssetContainerStatus(assetContainer.id, $event)" />

        <yard-view-asset-container-state-select
          :container-state="assetContainer.state"
          entry-point="Grid View"
          :asset-container="assetContainer"
          @input="value => updateAssetContainerState(assetContainer.id, value)" />
      </div>
      <v-divider />
    </div>

    <div>
      <v-icon size="x-small">mdi-map-marker</v-icon>
      <strong class="font-size-x-small ml-1">Arrived at</strong>
      <div>
        {{ arrivedTimeFormatted }}
      </div>
    </div>

    <div v-if="appointment?.id" class="font-size-x-small d-flex flex-column gap-3">
      <p class="d-flex flex-column" v-if="appointment?.refNumber">
        <strong>
          {{ getTranslatedRefNumberDisplayName($refNumSettings(selectedWarehouse).displayName) }}
        </strong>
        {{ appointment.refNumber }}
      </p>

      <p class="d-flex flex-column" v-if="appointment.dock">
        <strong>{{ t('yard.headers.plannedDock') }}</strong>
        <span>{{ appointment.dock.name }}</span>
      </p>

      <p class="d-flex flex-column" v-if="appointment.loadType">
        <strong>{{ t('global.specialtyTerms.loadTypes') }}</strong>
        <span>{{ appointment.loadType.name }} ({{ appointment.loadType.direction }})</span>
      </p>

      <div class="d-flex flex-column" v-if="appointment?.id && scheduledFor?.date">
        <strong>
          <v-icon class="mr-1">mdi-calendar</v-icon>
          {{ t('yard.messages.scheduledFor') }}
        </strong>

        <span>{{ scheduledFor.date }}</span>
        <span>{{ scheduledFor.start }} - {{ scheduledFor.end }}</span>

        <p class="my-2" v-if="appointment.status">
          <v-chip :color="appointment.status.toLowerCase()" size="x-small" pill>
            {{ t(`global.appointmentStatuses.${appointment.status}`).toUpperCase() }}
          </v-chip>
        </p>
        <a
          href="#"
          @click="emitViewAppointment()"
          :data-testid="`yard-view-asset-visit-view-appointment-${assetVisit.id}`">
          {{ t('yard.links.viewAppointment') }}
        </a>
      </div>
    </div>
    <div v-else>
      <v-icon size="x-small">mdi-calendar</v-icon>
      <strong class="font-size-x-small ml-1">{{ t('yard.messages.scheduledFor') }}</strong>
      <p class="text-error mb-4">
        <small>
          <v-icon size="small">mdi-information-outline</v-icon>
          {{ t('yard.messages.noAppointmentAssociated') }}
        </small>
      </p>

      <yard-view-link-appointment-button
        :asset-visit="assetVisit"
        @open-create-appointment-dialog="isCreateAppointmentDialogVisible = true"
        @open-link-appointment-dialog="isLinkAppointmentDialogVisible = true" />
    </div>

    <v-expansion-panels flat class="asset-details-data-panels">
      <v-expansion-panel v-if="assetVisit">
        <v-expansion-panel-title>
          <p>{{ t('yard.pages.yardView.headers.checkInDetails') }}</p>
        </v-expansion-panel-title>
        <v-expansion-panel-text class="pl-1">
          <asset-visit-form-data
            variant="list"
            size="small"
            :asset-visit="assetVisit"
            :warehouse="selectedWarehouse" />

          <v-btn
            variant="outlined"
            block
            size="small"
            class="my-4"
            @click="isAssetVisitChatVisible = true"
            append-icon="mdi-arrow-right">
            {{ t('yard.buttons.driversChat') }}
          </v-btn>
        </v-expansion-panel-text>
      </v-expansion-panel>
      <template v-if="isNavtracIntegrationEnabled">
        <v-expansion-panel :data-testid="'navtrac-details'">
          <v-expansion-panel-title>
            <p>{{ t('yard.headers.navTracDetails') }}</p>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <navtrac-observation-data
              :observation-event-match-time-window="
                selectedWarehouse?.settings.observationEventMatchTimeWindow
              "
              :asset-visit="assetVisit" />
          </v-expansion-panel-text>
        </v-expansion-panel>
      </template>
      <template v-if="assetContainersWithSameApptDrop.length && !isLiveVisit && !isPickupVisit">
        <v-expansion-panel>
          <v-expansion-panel-title>
            <p>{{ t('yard.messages.dropoffAssetLocation') }}</p>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <div
              v-for="assetContainer in assetContainersWithSameApptDrop"
              :key="assetContainer.id"
              class="mb-4">
              <div class="d-flex flex-column">
                <p class="typography-body-md-bold mt-3 mb-1">
                  <v-icon class="mr-1">mdi-truck-trailer</v-icon>
                  {{ assetContainer.code }}
                </p>

                <div v-if="assetContainer.spot">
                  <p>{{ assetContainer.spotArea.name }} - {{ assetContainer.spot.code }}</p>
                  <a
                    href="#"
                    @click="selectedSpot = assetContainer.spot"
                    v-if="
                      !assetContainer.spot.assetVisitId &&
                      assetContainer.spot.spotAssignments?.[0]?.id !== spotAssignment?.id
                    ">
                    {{ t('yard.buttons.goToSpot') }}
                  </a>
                  <strong v-else>{{ t('yard.messages.sameSpot') }}</strong>
                </div>
                <span v-else-if="isAssetContainerAttached(assetContainer)">
                  {{ t('yard.messages.assetAttached') }}
                </span>
                <span v-else>{{ t('yard.messages.assetIsNotAvailableToPickup') }}</span>
              </div>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </template>
      <template v-if="assetVisit?.appointment?.id && !isLiveVisit && !isDropVisit">
        <v-expansion-panel>
          <v-expansion-panel-title>
            <p>{{ t('yard.messages.pickupAssetLocation') }}</p>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <div v-if="!assetContainersWithSameApptPickup.length" class="text-center">
              <v-icon size="small" color="grey" class="mb-2">mdi-calendar-search</v-icon>
              <p class="typography-body-xs text-tertiary">
                {{ t('yard.messages.weCouldNotLocateAnyPickupAssetLinkedToTheAppointment') }}
              </p>
            </div>
            <div
              v-else
              v-for="assetContainer in assetContainersWithSameApptPickup"
              :key="assetContainer.id"
              class="mb-4">
              <div class="d-flex flex-column">
                <p class="typography-body-md-bold mt-3 mb-1">
                  <v-icon class="mr-1">mdi-truck-trailer</v-icon>
                  {{ assetContainer.code }}
                </p>

                <div v-if="assetContainer.spot">
                  <p>{{ assetContainer.spotArea.name }} - {{ assetContainer.spot.code }}</p>
                  <a
                    href="#"
                    @click="assignToAssetContainerSpot(assetContainer)"
                    v-if="
                      !assetContainer.spot.assetVisitId &&
                      assetContainer.spot.spotAssignments?.[0]?.id !== spotAssignment?.id
                    ">
                    {{ t('yard.links.assignAndHook') }}
                  </a>
                  <strong v-else>{{ t('yard.messages.sameSpot') }}</strong>
                </div>
                <span v-else-if="isAssetContainerAttached(assetContainer)">
                  {{ t('yard.messages.assetAttached') }}
                </span>
                <span v-else>{{ t('yard.messages.assetIsNotAvailableToPickup') }}</span>
              </div>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </template>
      <template v-if="assetVisit.assetContainers?.length > 0">
        <v-expansion-panel>
          <v-expansion-panel-title>
            <p>{{ t('yard.pages.yardView.headers.assetDetails') }}</p>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <div
              v-for="assetContainer in assetVisit.assetContainers"
              :key="assetContainer.id"
              class="asset-container-data">
              <yard-view-asset-container-form-data
                is-editable
                :triggers="triggers"
                :asset-container="assetContainer"
                :warehouse="selectedWarehouse" />
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </template>
      <feature-flag name="enable-asset-audit-log">
        <template #enabled>
          <v-expansion-panel>
            <v-expansion-panel-title>
              <p>{{ t('yard.headers.events') }}</p>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <yard-view-asset-visit-timeline :asset-visit-events="assetVisitEvents" />
            </v-expansion-panel-text>
          </v-expansion-panel>
        </template>
      </feature-flag>
    </v-expansion-panels>

    <link-appointment-dialog
      :show-dialog="isLinkAppointmentDialogVisible"
      @close="isLinkAppointmentDialogVisible = false"
      @linked="handleAppointmentLinked"
      external-activator
      :reference-number-settings="selectedWarehouse.settings || {}"
      :warehouse="selectedWarehouse"
      :asset-to-link="assetVisit"
      :appointment-days-threshold="15"
      :follow-to-appointment-details="false"
      :is-military-time-enabled="isMilitaryTimeEnabled" />

    <create-appointment-dialog
      :show-dialog="isCreateAppointmentDialogVisible"
      @close="isCreateAppointmentDialogVisible = false"
      @scheduled="handleNewAppointment"
      :follow-to-appointment-details="false"
      :button-label="t('yard.common.phrases.createAppointment')"
      :asset-visit="assetVisit"
      external-activator
      :context="{ selectedWarehouse }" />
  </div>
</template>

<script>
import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { FeatureFlag, useFlag } from '@loadsmart/vue-feature-flags';
import useYard from '@/modules/yard/composables/useYard';
import {
  AssetVisitType,
  getTranslatedRefNumberDisplayName,
  isFeatureEnabledForWarehouse,
  LuxonDateTimeFormats,
  WarehouseFeatureEnum
} from '@nova/core';
import YardViewSpotSelect from '@/modules/yard/components/Selects/YardViewSpotSelect.vue';
import { useEventHub, useNotify } from '@/composables';
import YardViewLinkAppointmentButton from '../../YardViewLinkAppointmentButton.vue';
import LinkAppointmentDialog from '@/components/elements/LinkAppointmentDialog.vue';
import CreateAppointmentDialog from '@/modules/appointments/components/CreateAppointmentDialog.vue';
import YardViewAssetContainerStatusSelect from '@/modules/yard/components/Selects/YardViewAssetContainerStatusSelect.vue';
import YardViewAssetVisitTimeline from '@/modules/yard/components/AssetVisit/Timeline/YardViewAssetVisitTimeline.vue';
import NavtracObservationData from '@/components/elements/NavtracObservationData.vue';
import YardViewAssetContainerFormData from '@/modules/yard/components/AssetContainer/YardViewAssetContainerFormData.vue';
import YardViewAssetContainerStateSelect from '@/modules/yard/components/Selects/YardViewAssetContainerStateSelect.vue';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'YardViewAssetVisitDetailData',
  methods: { getTranslatedRefNumberDisplayName },
  components: {
    YardViewSpotSelect,
    YardViewAssetContainerStatusSelect,
    YardViewLinkAppointmentButton,
    LinkAppointmentDialog,
    CreateAppointmentDialog,
    YardViewAssetContainerFormData,
    YardViewAssetVisitTimeline,
    YardViewAssetContainerStateSelect,
    NavtracObservationData,
    FeatureFlag
  },
  emits: ['assign', 'view-appointment'],
  props: {
    assetVisit: {
      type: Object,
      required: true
    },
    warehouse: {
      type: Object,
      required: false,
      default: null
    },
    showAssignSpotSelect: {
      type: Boolean,
      default: true
    },
    allowContainerStatusUpdate: {
      type: Boolean,
      default: false
    },
    spotAssignment: {
      type: Object,
      required: false,
      default: () => {}
    }
  },
  setup(props) {
    const { t } = useI18n();
    const yardStore = useYard();
    const appointment = computed(() => {
      return props.assetVisit?.appointment;
    });

    const selectedWarehouse = yardStore.selectedWarehouseRef;

    const isCreateAppointmentDialogVisible = ref(false);
    const isLinkAppointmentDialogVisible = ref(false);

    const assetContainersWithSameApptPickup = ref([]);
    const assetContainersWithSameApptDrop = ref([]);

    const notify = useNotify();

    const eventHub = useEventHub();

    const truckIcon = computed(() => yardStore.getTruckIcon(props.assetVisit?.visitType));

    const scheduledFor = computed(() => yardStore.scheduledFor(appointment.value));

    const arrivedTimeFormatted = computed(() => {
      return yardStore.formatDateTime(
        props.assetVisit?.createDateTime,
        LuxonDateTimeFormats.ShortDayDateSlashed12Hour,
        LuxonDateTimeFormats.ShortDayDateSlashed24Hour
      );
    });

    const isLiveVisit = computed(() => {
      return props.assetVisit.visitType === AssetVisitType.Live;
    });

    const isDropVisit = computed(() => {
      return props.assetVisit.visitType === AssetVisitType.Drop;
    });

    const isPickupVisit = computed(() => {
      return props.assetVisit.visitType === AssetVisitType.PickUp;
    });

    const featureFlagNavtracUIEnabled = useFlag('enable-navtrac-details-ui')?.value.enabled;

    const isNavtracIntegrationEnabled = computed(() => {
      return (
        isFeatureEnabledForWarehouse(selectedWarehouse.value, WarehouseFeatureEnum.NavTrac) &&
        featureFlagNavtracUIEnabled
      );
    });

    const getAssetContainerStatus = assetContainerEvents =>
      yardStore.getAssignedAssetContainerStatus(assetContainerEvents || []);

    const updateAssetContainerStatus = (assetContainerId, eventType) => {
      yardStore.updateAssetContainerStatus(assetContainerId, eventType);
    };

    const updateAssetContainerState = async (assetContainerId, state) =>
      yardStore
        .updateAssetContainer(assetContainerId, { state })
        .then(assetContainer => eventHub.$emit('update-AssetContainer', assetContainer));

    const handleAppointmentLinked = async ({ appointmentId }) => {
      notify(t('yard.messages.appointmentLinkedSuccessfully'), 'success');

      isLinkAppointmentDialogVisible.value = false;

      yardStore.trackMixpanelEvent(yardStore.MixPanelEvent.LINK_ASSET_VISIT_APPOINTMENT, {
        assetVisit: props.assetVisit,
        spot: yardStore.selectedSpotRef.value,
        entryPoint: 'Live Yard View Panel',
        metadata: {
          appointmentId,
          existing_appointment: true
        }
      });

      updateAfterAppointmentChange();
    };

    const updateAfterAppointmentChange = async () => {
      if (yardStore.selectedAssetVisitRef.value?.id === props.assetVisit.id) {
        await yardStore.fetchAssetVisits();

        yardStore.selectedAssetVisitRef.value = yardStore.waitingAssetVisitsRef.value.find(
          visit => visit.id === props.assetVisit.id
        );
      } else {
        yardStore.fetchSpots();
      }
    };

    const handleNewAppointment = async appointment => {
      const apptWithUser = (
        await axios.get(`appointment/${appointment?.id}`, {
          params: { select: 'id', join: 'user||companyId' }
        })
      )?.data?.data;

      try {
        await axios.post('/asset-visit/link-unplanned-checkin-to-appointment', {
          assetVisitId: props.assetVisit.id,
          appointmentId: appointment?.id,
          companyId: apptWithUser?.user?.companyId,
          isPlanned: false
        });

        yardStore.trackMixpanelEvent(yardStore.MixPanelEvent.LINK_ASSET_VISIT_APPOINTMENT, {
          assetVisit: props.assetVisit.id,
          spot: yardStore.selectedSpotRef.value,
          entryPoint: 'Live Yard View Panel',
          metadata: {
            appointmentId: appointment.id,
            existing_appointment: false
          }
        });
        updateAfterAppointmentChange();
      } catch (error) {
        notify(t('yard.messages.failedToCreateAppointment'), 'error');
        console.error(error);
        return;
      } finally {
        isCreateAppointmentDialogVisible.value = false;
      }
    };

    const assignToAssetContainerSpot = async assetContainer => {
      const assetContainerSpot = assetContainer.spot;
      const assetContainerSpotAssignment = assetContainerSpot?.spotAssignments?.[0];

      // Do not reassign if the asset container is already assigned to the same spot
      if (assetContainerSpotAssignment?.id === props.spotAssignment?.id) {
        return;
      }

      let spotAssignment = null;

      if (props.spotAssignment?.id) {
        spotAssignment = await yardStore.reassignSpotAssignment(props.spotAssignment?.id, {
          spotId: assetContainerSpot.id,
          reason: 'Assign and hook asset container'
        });
      } else {
        spotAssignment = await yardStore.updateSpotAssignment(assetContainerSpotAssignment?.id, {
          assetVisitId: props.assetVisit.id,
          spotId: assetContainerSpot.id
        });
      }

      eventHub.$emit(yardStore.SPOT_ASSIGNMENT_CHANGED, {
        assetVisit: props.assetVisit,
        newSpotCode: spotAssignment.spot.code,
        newSpotAreaName: spotAssignment.spot.spotArea.name
      });

      await yardStore.attachAssetVisit(props.assetVisit.id, { [assetContainer.id]: 'attach' });

      yardStore.trackMixpanelEvent(yardStore.MixPanelEvent.HOOK, {
        assetVisit: props.assetVisit,
        metadata: {
          asset_container_id: assetContainer.id,
          asset_container_type: assetContainer.type,
          appointment_id: props.assetVisit.appointmentId
        }
      });
    };

    const setAssetContainerData = (assetContainersRef, res) => {
      if (Array.isArray(res?.data?.data) && res.data.data.length) {
        assetContainersRef.value = res.data.data;
        assetContainersRef.value.forEach(assetContainer => {
          assetContainer.spot = yardStore.spotsRef.value.find(spot =>
            spot.spotAssignments.find(
              assignment => assignment.assetContainerId === assetContainer.id
            )
          );
          if (assetContainer.spot) {
            assetContainer.spotArea = yardStore.spotAreasRef.value.find(
              area => area.id === assetContainer.spot.spotAreaId
            );
          }
        });
      }
    };

    const checkPickupAppointment = async () => {
      if (!appointment.value?.id) {
        return;
      }

      try {
        const res = await axios.get(`asset-container`, {
          params: {
            s: {
              pickupAppointmentId: appointment.value.id
            },
            select: ['id,code,type,pickupAppointmentId,dropAppointmentId']
          }
        });
        setAssetContainerData(assetContainersWithSameApptPickup, res);
      } catch (error) {
        console.warn('Failed to check if asset container has same appointment', error);
      }
    };

    const checkDropoffAppointment = async () => {
      if (!appointment.value?.id) {
        return;
      }

      try {
        const res = await axios.get(`asset-container`, {
          params: {
            s: {
              dropAppointmentId: appointment.value.id
            },
            select: ['id,code,type,pickupAppointmentId,dropAppointmentId']
          }
        });
        setAssetContainerData(assetContainersWithSameApptDrop, res);
      } catch (error) {
        console.warn('Failed to check if asset container has same appointment', error);
      }
    };

    const isAssetContainerAttached = assetContainer =>
      Boolean(props.assetVisit.assetContainers?.find(ac => ac.id === assetContainer.id)?.id);

    onMounted(() => {
      checkPickupAppointment();
      checkDropoffAppointment();
    });

    watch(
      () => props.assetVisit,
      () => {
        checkPickupAppointment();
        checkDropoffAppointment();
      }
    );

    return {
      getAssetContainerStatus,
      updateAssetContainerStatus,
      getAssetVisitTypeDisplayName: yardStore.getAssetVisitTypeDisplayName,
      isLiveVisit,
      isPickupVisit,
      isDropVisit,
      appointment,
      truckIcon,
      arrivedTimeFormatted,
      selectedSpot: yardStore.selectedSpotRef,
      scheduledFor,
      isAssetVisitChatVisible: yardStore.isAssetVisitChatVisibleRef,
      isNavtracIntegrationEnabled,
      emitViewAppointment: () =>
        eventHub.emit('yard-view-appointment', {
          appointmentId: appointment.value?.id,
          assetVisitId: props.assetVisit?.id
        }),
      selectedWarehouse,
      updateAssetContainerState,
      isMilitaryTimeEnabled: yardStore.isMilitaryTimeEnabled,
      isCreateAppointmentDialogVisible,
      isLinkAppointmentDialogVisible,
      handleAppointmentLinked,
      handleNewAppointment,
      assignToAssetContainerSpot,
      assetContainersWithSameApptPickup,
      assetContainersWithSameApptDrop,
      isAssetContainerAttached,
      triggers: yardStore.assetContainerTriggersRef,
      assetVisitEvents: props.assetVisit.assetVisitEvents ?? [],
      getTranslatedCompanyName: yardStore.getTranslatedCompanyName,
      getTranslatedAssetContainerType: yardStore.getTranslatedAssetContainerType,
      t
    };
  }
});
</script>
<style lang="scss">
.asset-details-data-panels {
  gap: 0;

  .v-expansion-panel-text__wrapper {
    padding: 0 !important;
  }

  .v-expansion-panel-title {
    @extend .typography-heading-sm-bold;
    padding: $spacing-4 0 !important;
  }

  .v-expansion-panel {
    border-top: 1px solid $color-line-border;
  }
}
.asset-container-data {
  margin-bottom: $spacing-4 !important;
  border-bottom: 1px solid $color-line-border;

  &:last-child {
    border-bottom: none;
  }
}
</style>
